// Attachment thumbnail — the small picture on a file's card.
//
// The server makes one WebP per file (an image scaled down, page 1 of a
// PDF) and this draws it. Fetched through `load` with the Authorization
// header, so the URL carries no token; the browser keeps the bytes and
// revalidates them (a bodyless 304 behind the read gate) on later visits.
// Within a session each attachment is fetched once and the object URL is
// shared by every card that shows it.
//
// No thumbnail (the type has none, the render failed, the request failed)
// → `fallback`, which is whatever the card drew before thumbnails existed.

import React, { useEffect, useState } from 'react';
import { thumbKind } from './AttachmentPreview';

// attachment id -> Promise<string|null> (object URL, or null = none).
// Session-long on purpose: attachments are immutable under their id and a
// thumbnail is ~20 KB, so nothing here is worth revoking.
const _urls = new Map();

function thumbUrl(att, load) {
  const id = String(att.id);
  if (!_urls.has(id)) {
    _urls.set(id, load(att)
      .then((blob) => (blob && blob.size ? URL.createObjectURL(blob) : null))
      .catch((err) => {
        // A 404 is the server's settled answer; anything else (offline, a
        // 5xx) may succeed next time the card mounts.
        if (err?.response?.status !== 404) _urls.delete(id);
        return null;
      }));
  }
  return _urls.get(id);
}

function AttachmentThumb({ attachment, load, fallback = null, className = 'attachment-thumb-img' }) {
  const kind = thumbKind(attachment?.filename, attachment?.mimeType);
  const [url, setUrl] = useState(null);
  const [settled, setSettled] = useState(!kind || !load);
  const id = attachment?.id;

  useEffect(() => {
    if (!kind || !load || id == null) { setUrl(null); setSettled(true); return undefined; }
    let live = true;
    setSettled(false);
    thumbUrl(attachment, load).then((u) => { if (live) { setUrl(u); setSettled(true); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, kind]);

  if (url) {
    return <img className={`${className} ${className}--${kind}`} src={url} alt={attachment.filename} onError={() => setUrl(null)} />;
  }
  // While the thumbnail is on its way the card stays empty rather than
  // flashing the fallback (for an image, the fallback is the full file).
  return settled ? fallback : null;
}

export default AttachmentThumb;
